import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { WebSocketService } from './websocket.service';
import { isVideoUploadSettled } from './upload-readiness';
import { nextStatusPollDelayMs } from '../components/video-upload-form/video-processing-progress';

export interface GlobalUploadStatus {
  isActive: boolean;
  type: 'video' | 'document' | 'image';
  status: 'idle' | 'uploading' | 'processing' | 'completed' | 'error';
  progress: number; // 0-100 for upload progress
  processingProgress: number; // 0-100 for processing progress
  fileName: string;
  message: string;
  uploadId: string; // Unique identifier for this upload
  startTime: Date;
  canNavigateBack?: boolean; // Whether user can click to return to upload page
  navigateBackRoute?: string; // Route to navigate back to
  videoId?: string; // Store video ID for status checking after refresh
}

export interface QueuedUpload {
  uploadId: string;
  config: {
    type: 'video' | 'document' | 'image';
    fileName: string;
    canNavigateBack?: boolean;
    navigateBackRoute?: string;
  };
  status: 'queued' | 'uploading' | 'processing' | 'completed' | 'error';
  videoId?: string; // Video ID from database (for videos created immediately)
  bunnyVideoId?: string; // Bunny video ID (for videos created immediately)
  startCallback?: () => void; // Called when this upload starts
}

@Injectable({
  providedIn: 'root'
})
export class GlobalUploadStatusService {
  private readonly STORAGE_KEY = 'coursebuilder_upload_status';
  private readonly QUEUE_STORAGE_KEY = 'coursebuilder_upload_queue';
  private uploadStatusSubject = new BehaviorSubject<GlobalUploadStatus>(this.getInitialStatus());
  private uploadQueue: QueuedUpload[] = [];
  private isProcessingQueue = false;

  public uploadStatus$: Observable<GlobalUploadStatus> = this.uploadStatusSubject.asObservable();

  constructor(private websocketService: WebSocketService) {
    // Subscribe to changes and persist to localStorage
    this.uploadStatus$.subscribe(status => {
      this.saveToStorage(status);
    });

    // Subscribe to WebSocket video status updates for real-time completion
    this.websocketService.videoStatusUpdates$.subscribe((update) => {
      const currentStatus = this.uploadStatusSubject.value;

      // Check if this update is for the current upload
      if (currentStatus.isActive && currentStatus.videoId === update.videoId) {
        console.log('[GlobalUploadStatusService] WebSocket update for current upload:', update);

        if (update.status.isReady) {
          this.completeUpload(currentStatus.uploadId);
        } else if (update.status.hasError) {
          this.setUploadError(currentStatus.uploadId, 'Video processing failed');
        }
      }
    });

    // Warn user before leaving page if uploads are active
    window.addEventListener('beforeunload', (event) => {
      if (this.hasActiveUploads()) {
        const message = 'You have active uploads in progress. Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message; // Standard way to show confirmation dialog
        return message;
      }
      return undefined;
    });
  }

  // Get initial status from localStorage or default
  private getInitialStatus(): GlobalUploadStatus {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Restore startTime as Date object
        parsed.startTime = new Date(parsed.startTime);

        // Only restore if the upload is still active and recent (within 5 minutes)
        // Reduced from 24 hours to prevent stuck uploads after page refresh
        const minutesSinceStart = (Date.now() - parsed.startTime.getTime()) / (1000 * 60);

        // Don't restore 'uploading' status - uploads can't resume after refresh
        // Only restore 'processing' status since processing continues server-side
        if (parsed.isActive && minutesSinceStart < 5) {
          if (parsed.status === 'uploading') {
            console.log('[GlobalUploadStatusService] Skipping restore of uploading status after refresh - upload cannot resume');

            // If this upload had a videoId, the video was created but upload was interrupted
            // Store this videoId so we can mark it as failed
            if (parsed.videoId) {
              console.warn('[GlobalUploadStatusService] Upload was interrupted for video:', parsed.videoId);
              // Store interrupted videoId in sessionStorage so content hub can handle cleanup
              sessionStorage.setItem('interrupted_upload_video', parsed.videoId);
            }

            localStorage.removeItem(this.STORAGE_KEY);
          } else if (parsed.status === 'processing' && parsed.videoId) {
            console.log('[GlobalUploadStatusService] Restoring processing status from localStorage:', parsed);
            return parsed;
          }
        } else {
          // Clear stale status
          console.log('[GlobalUploadStatusService] Clearing stale upload status (age:', minutesSinceStart, 'minutes)');
          localStorage.removeItem(this.STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error('Error restoring upload status:', error);
      localStorage.removeItem(this.STORAGE_KEY);
    }

    // Return default status
    return {
      isActive: false,
      type: 'video',
      status: 'idle',
      progress: 0,
      processingProgress: 0,
      fileName: '',
      message: '',
      uploadId: '',
      startTime: new Date()
    };
  }

  // Save status to localStorage
  private saveToStorage(status: GlobalUploadStatus): void {
    try {
      if (status.isActive) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(status));
      } else {
        localStorage.removeItem(this.STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error saving upload status to localStorage:', error);
    }
  }

  // Start a new upload process (adds to queue if another upload is active)
  startUpload(config: {
    type: 'video' | 'document' | 'image';
    fileName: string;
    canNavigateBack?: boolean;
    navigateBackRoute?: string;
  }, startCallback?: () => void, videoId?: string, bunnyVideoId?: string): string {
    const uploadId = Date.now().toString() + Math.random().toString(36).substr(2, 9);

    const queuedUpload: QueuedUpload = {
      uploadId,
      config,
      status: 'queued',
      videoId,
      bunnyVideoId,
      startCallback
    };

    // Add to queue
    this.uploadQueue.push(queuedUpload);
    console.log('[GlobalUploadStatusService] Added upload to queue:', uploadId, 'videoId:', videoId, 'Queue length:', this.uploadQueue.length);

    // Process queue if not already processing
    this.processNextInQueue();

    return uploadId;
  }

  // Process the next upload in the queue
  private processNextInQueue(): void {
    // If already processing, don't start another
    if (this.isProcessingQueue) {
      console.log('[GlobalUploadStatusService] Already processing an upload, waiting...');
      return;
    }

    // Get next queued upload
    const nextUpload = this.uploadQueue.find(u => u.status === 'queued');
    if (!nextUpload) {
      console.log('[GlobalUploadStatusService] No uploads in queue');
      return;
    }

    console.log('[GlobalUploadStatusService] Starting upload from queue:', nextUpload.uploadId);

    // Mark as processing
    this.isProcessingQueue = true;
    nextUpload.status = 'uploading';

    // Update global status with videoId so content cards can match
    this.uploadStatusSubject.next({
      isActive: true,
      type: nextUpload.config.type,
      status: 'uploading',
      progress: 0,
      processingProgress: 0,
      fileName: nextUpload.config.fileName,
      message: 'Uploading...',
      uploadId: nextUpload.uploadId,
      startTime: new Date(),
      canNavigateBack: nextUpload.config.canNavigateBack || false,
      navigateBackRoute: nextUpload.config.navigateBackRoute,
      videoId: nextUpload.videoId // Include videoId so content cards can display upload progress
    });

    // Call the start callback if provided
    if (nextUpload.startCallback) {
      setTimeout(() => nextUpload.startCallback!(), 100);
    }
  }

  // Set videoId for an active upload (call this after video record is created)
  setVideoId(uploadId: string, videoId: string): void {
    const current = this.uploadStatusSubject.value;
    if (current.uploadId === uploadId && current.isActive) {
      console.log('[GlobalUploadStatusService] Setting videoId:', videoId, 'for uploadId:', uploadId);
      this.uploadStatusSubject.next({
        ...current,
        videoId
      });
    }
  }

  // Update upload progress
  updateUploadProgress(uploadId: string, progress: number): void {
    const current = this.uploadStatusSubject.value;
    if (current.uploadId === uploadId && current.isActive) {
      this.uploadStatusSubject.next({
        ...current,
        progress,
        message: 'Uploading...'
      });
    }
  }

  // Transition to processing phase
  startProcessing(uploadId: string, videoId?: string): void {
    const current = this.uploadStatusSubject.value;
    if (current.uploadId === uploadId && current.isActive) {
      const finalVideoId = videoId || current.videoId;
      console.log('[GlobalUploadStatusService] Starting processing phase. uploadId:', uploadId, 'videoId:', finalVideoId);
      this.uploadStatusSubject.next({
        ...current,
        status: 'processing',
        progress: 100,
        processingProgress: 0,
        message: 'Processing...',
        videoId: finalVideoId
      });

      // Update queue status to processing
      const queuedUpload = this.uploadQueue.find(u => u.uploadId === uploadId);
      if (queuedUpload) {
        queuedUpload.status = 'processing';
      }

      // Upload phase is complete - start next upload in queue immediately
      // Processing can happen in parallel for multiple videos
      console.log('[GlobalUploadStatusService] Upload phase complete, starting next upload in queue');
      this.isProcessingQueue = false;
      this.processNextInQueue();
    }
  }

  // Update processing progress
  updateProcessingProgress(uploadId: string, progress: number): void {
    const current = this.uploadStatusSubject.value;

    if (current.uploadId === uploadId && current.isActive && current.status === 'processing') {
      this.uploadStatusSubject.next({
        ...current,
        processingProgress: progress,
        message: 'Processing...'
      });
    }
  }

  // Mark upload as completed
  completeUpload(uploadId: string): void {
    const current = this.uploadStatusSubject.value;

    if (current.uploadId === uploadId && current.isActive) {
      this.uploadStatusSubject.next({
        ...current,
        status: 'completed' as const,
        processingProgress: 100,
        message: 'Complete'
      });

      // Update queue
      const queuedUpload = this.uploadQueue.find(u => u.uploadId === uploadId);
      if (queuedUpload) {
        queuedUpload.status = 'completed';
      }

      // Auto-hide after 5 seconds and remove from queue
      setTimeout(() => {
        this.hideUploadStatus(uploadId);

        // Just remove from queue - next upload already started in startProcessing()
        this.uploadQueue = this.uploadQueue.filter(u => u.uploadId !== uploadId);
        console.log('[GlobalUploadStatusService] Removed completed upload from queue:', uploadId, 'Remaining:', this.uploadQueue.length);
      }, 5000);
    } else {
      console.warn('[GlobalUploadStatusService] Upload ID mismatch or not active', {
        expected: uploadId,
        current: current.uploadId,
        isActive: current.isActive
      });
    }
  }

  // Mark upload as error
  setUploadError(uploadId: string, errorMessage: string): void {
    const current = this.uploadStatusSubject.value;
    if (current.uploadId === uploadId && current.isActive) {
      this.uploadStatusSubject.next({
        ...current,
        status: 'error',
        message: `Upload failed: ${errorMessage}`
      });

      // Update queue
      const queuedUpload = this.uploadQueue.find(u => u.uploadId === uploadId);
      if (queuedUpload) {
        queuedUpload.status = 'error';
      }

      // Wait 3 seconds then remove from queue and start next upload
      setTimeout(() => {
        this.uploadQueue = this.uploadQueue.filter(u => u.uploadId !== uploadId);
        this.isProcessingQueue = false;
        console.log('[GlobalUploadStatusService] Removed failed upload from queue:', uploadId, 'Starting next...');
        this.processNextInQueue();
      }, 3000);
    }
  }

  /**
   * KAN-700: the user cancelled this upload. Drop it from the queue so a
   * queued upload never starts, and if it held the transfer slot, free it for
   * the next upload. Other uploads are untouched (unlike cancelAllUploads).
   */
  cancelUpload(uploadId: string): void {
    const entry = this.uploadQueue.find(u => u.uploadId === uploadId);
    this.uploadQueue = this.uploadQueue.filter(u => u.uploadId !== uploadId);

    const current = this.uploadStatusSubject.value;
    if (current.uploadId === uploadId) {
      this.uploadStatusSubject.next({ ...current, isActive: false, status: 'idle' });
    }

    if (entry?.status === 'uploading') {
      this.isProcessingQueue = false;
      this.processNextInQueue();
    }
  }

  // Hide upload status
  hideUploadStatus(uploadId: string): void {
    const current = this.uploadStatusSubject.value;
    if (current.uploadId === uploadId) {
      this.uploadStatusSubject.next({
        ...current,
        isActive: false
      });
    }
  }

  // Get current upload status
  getCurrentStatus(): GlobalUploadStatus {
    return this.uploadStatusSubject.value;
  }

  // Check if there's an active upload
  isUploadActive(): boolean {
    return this.uploadStatusSubject.value.isActive;
  }

  // Resume upload status checking after page refresh
  resumeUploadChecking(uploadId: string, videoId: string, httpClient: any, apiUrl: string): void {
    const current = this.uploadStatusSubject.value;
    if (current.uploadId === uploadId && current.isActive) {
      if (current.status === 'processing' || current.status === 'uploading') {
        // Check actual video status from database
        this.checkActualVideoStatus(uploadId, videoId, httpClient, apiUrl);
      }
    }
  }

  // Check actual video status from database (DEPRECATED - WebSocket handles this now)
  private checkActualVideoStatus(uploadId: string, videoId: string, httpClient: any, apiUrl: string): void {
    // Skip polling if WebSocket is connected - it will handle updates
    if (this.websocketService.isConnected()) {
      console.log('[GlobalUploadStatusService] WebSocket connected, skipping status polling');
      return;
    }

    console.log('[GlobalUploadStatusService] WebSocket not connected, using fallback polling');

    httpClient.get(`${apiUrl}/videos/${videoId}`).subscribe({
      next: (response: any) => {
        const video = response.data || response.video || response;

        // KAN-507: an upload is finished when the video can actually PLAY.
        // This used to complete as soon as the row carried a bunnyVideoId,
        // which is true from the instant the upload starts - so the progress UI
        // reported success while the encode had barely begun.
        if (video && video.id) {
          if (video.status === 'failed' || video.status === 'unavailable') {
            this.setUploadError(uploadId, 'Video processing failed');
            return;
          }

          if (isVideoUploadSettled(video)) {
            this.completeUpload(uploadId);
            return;
          }
        }

        // Still processing, check again in 10 seconds (only if WebSocket still not connected)
        setTimeout(() => {
          if (!this.websocketService.isConnected()) {
            this.checkActualVideoStatus(uploadId, videoId, httpClient, apiUrl);
          }
        }, 10000);
      },
      error: (error: any) => {
        console.error('Error checking video status:', error);

        // If the error suggests the video was deleted (404), clear the status immediately
        if (error.status === 404 || error.message?.includes('not found')) {
          this.clearStuckStatus();
          return;
        }

        // KAN-532: a failed fetch is not a verdict. Declaring the upload
        // complete here is the same class of lie as declaring it failed.
        setTimeout(() => {
          if (!this.websocketService.isConnected()) {
            this.checkActualVideoStatus(uploadId, videoId, httpClient, apiUrl);
          }
        }, 30000);
      }
    });
  }

  // Private method to handle processing status checking after page refresh
  private startProcessingStatusCheck(uploadId: string, bunnyVideoId: string, bunnyVideoStatusService: any): void {
    let checkFailures = 0;

    const checkStatus = () => {
      bunnyVideoStatusService.checkVideoStatus(bunnyVideoId).subscribe({
        next: (status: any) => {
          // KAN-532: a failed status CHECK is not a failed ENCODE. It must not
          // error the upload and must not end the loop — that is exactly what
          // produced a false "Video processing failed" on a healthy video.
          if (status.checkFailed) {
            checkFailures += 1;
            setTimeout(checkStatus, nextStatusPollDelayMs(checkFailures));
            return;
          }

          checkFailures = 0;

          if (status.hasError) {
            this.setUploadError(uploadId, 'Video processing failed');
            return;
          }

          if (status.isReady) {
            this.completeUpload(uploadId);
            return;
          }

          setTimeout(checkStatus, nextStatusPollDelayMs(0));
        },
        error: (error: any) => {
          console.error('Error checking video status after refresh:', error);

          // A deleted video is a real answer: the row is gone, stop tracking it.
          if (error.status === 404 || error.message?.includes('not found')) {
            this.clearStuckStatus();
            return;
          }

          // KAN-532: anything else is unknown, not finished. Keep checking with
          // a backoff rather than declaring the upload complete on a blip.
          checkFailures += 1;
          setTimeout(checkStatus, nextStatusPollDelayMs(checkFailures));
        }
      });
    };

    // Start checking immediately
    checkStatus();
  }

  // Clear stuck upload status (useful when videos are deleted or uploads get stuck)
  clearStuckStatus(): void {
    this.uploadStatusSubject.next(this.getDefaultStatus());
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // Get current status (useful for debugging) - already exists above

  // Check if there's a stuck upload
  isStuck(): boolean {
    const current = this.uploadStatusSubject.value;
    if (!current.isActive) return false;
    
    const minutesSinceStart = (Date.now() - current.startTime.getTime()) / (1000 * 60);
    // Consider stuck if processing for more than 2 minutes, or if at 100% for more than 30 seconds
    const isLongProcessing = current.status === 'processing' && minutesSinceStart > 2;
    const isStuckAt100 = (current.progress >= 100 || current.processingProgress >= 100) && minutesSinceStart > 0.5;
    
    return isLongProcessing || isStuckAt100;
  }

  private getDefaultStatus(): GlobalUploadStatus {
    return {
      isActive: false,
      type: 'video',
      status: 'idle',
      progress: 0,
      processingProgress: 0,
      fileName: '',
      message: '',
      uploadId: '',
      startTime: new Date()
    };
  }

  // Get queue status
  getQueueStatus(): { queueLength: number; position: number | null } {
    const queueLength = this.uploadQueue.filter(u => u.status === 'queued').length;
    const currentStatus = this.uploadStatusSubject.value;

    // Find position in queue (1-indexed)
    let position: number | null = null;
    if (currentStatus.isActive) {
      const queuedUploads = this.uploadQueue.filter(u => u.status === 'queued');
      const index = queuedUploads.findIndex(u => u.uploadId === currentStatus.uploadId);
      position = index >= 0 ? index + 1 : null;
    }

    return { queueLength, position };
  }

  // Check if a specific upload is queued
  isUploadQueued(uploadId: string): boolean {
    const upload = this.uploadQueue.find(u => u.uploadId === uploadId);
    return upload?.status === 'queued' || false;
  }

  // Get upload status for a specific video by videoId
  getUploadByVideoId(videoId: string): QueuedUpload | null {
    return this.uploadQueue.find(u => u.videoId === videoId) || null;
  }

  // Get the position of a video in the queue
  getQueuePositionForVideo(videoId: string): number | null {
    const queuedUploads = this.uploadQueue.filter(u => u.status === 'queued');
    const index = queuedUploads.findIndex(u => u.videoId === videoId);
    return index >= 0 ? index + 1 : null;
  }

  // Check if there are any active uploads (uploading or queued, not processing)
  hasActiveUploads(): boolean {
    const current = this.uploadStatusSubject.value;
    // Only consider 'uploading' status as active (not processing, since that's server-side)
    const hasCurrentUpload = current.isActive && current.status === 'uploading';
    const hasQueuedUploads = this.uploadQueue.some(u => u.status === 'queued' || u.status === 'uploading');
    return hasCurrentUpload || hasQueuedUploads;
  }

  // Cancel all active uploads and clear queue
  cancelAllUploads(): void {
    console.log('[GlobalUploadStatusService] Cancelling all uploads and clearing queue');

    // Clear the queue
    this.uploadQueue = [];
    this.isProcessingQueue = false;

    // Clear current upload status
    const current = this.uploadStatusSubject.value;
    if (current.isActive) {
      this.uploadStatusSubject.next({
        ...current,
        isActive: false,
        status: 'idle'
      });
    }

    // Clear localStorage
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // Get and clear interrupted video ID (if any)
  getAndClearInterruptedVideoId(): string | null {
    const videoId = sessionStorage.getItem('interrupted_upload_video');
    if (videoId) {
      sessionStorage.removeItem('interrupted_upload_video');
      return videoId;
    }
    return null;
  }
}